import { usePathname } from 'expo-router';
import { useEffect, useRef, useState } from 'react';
import { Text } from 'react-native';

import { useScreenTranslations } from '@/hooks/useScreenTranslations';

import { styles } from './styles';
import type { RouteAnnouncementKey, RouteAnnouncerProps } from './types';

// Ordered longest match first; the booking paths nest under each other.
const ROUTE_ANNOUNCEMENTS: Array<[RegExp, RouteAnnouncementKey]> = [
  [/^\/booking\/book\/[^/]+\/barber\/[^/]+\/services\/?$/, 'chooseService'],
  [
    /^\/booking\/book\/[^/]+\/barber\/[^/]+\/(services\/[^/]+\/)?schedule\/?$/,
    'chooseTime',
  ],
  [/^\/booking\/gift-card\/[^/]+\/barber\/[^/]+\/?$/, 'buyGiftCard'],
  [/^\/booking\/book\/[^/]+\/?$/, 'chooseProfessional'],
  [/^\/booking\/brands\/[^/]+\/?$/, 'chooseLocation'],
];

export const getRouteAnnouncement = (
  pathname: string,
): RouteAnnouncementKey | null =>
  ROUTE_ANNOUNCEMENTS.find(([pattern]) => pattern.test(pathname))?.[1] ?? null;

const RouteAnnouncer = ({ shownCount = 0 }: RouteAnnouncerProps) => {
  const { t, i18n } = useScreenTranslations('RouteAnnouncer');
  const key = getRouteAnnouncement(usePathname());
  const [text, setText] = useState('');
  // `t` changes identity every render, so the effect keys on the language.
  const translate = useRef(t);
  translate.current = t;

  // Cleared and re-set a frame later so the same name announces again when
  // the customer returns to a screen they already heard, and on every open:
  // the app boots while the frame is still hidden, so the first screen's
  // name must be written again once the host reports the frame shown.
  useEffect(() => {
    setText('');
    if (!key) return;
    const frame = requestAnimationFrame(() => setText(translate.current(key)));
    return () => cancelAnimationFrame(frame);
  }, [key, shownCount, i18n.language]);

  if (!key) return null;

  return (
    <Text testID="route-announcer" aria-live="polite" style={styles.hidden}>
      {text}
    </Text>
  );
};

export { RouteAnnouncer };
